<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <label
        class="el-radio qa-radio"
        :class="{
            'is-disabled': isDisabled,
            'is-checked': isChecked,
            'is-focus': isFocused,
            'is-active': isActive,
            'is-align-center': align === 'center'
        }"
        :title="computedTitle"
        @keydown.enter.stop.prevent="handleKeyDown"
        @keyup.enter.stop.prevent="handleKeyUp"
    >
        <span class="el-radio__content qa-radio-content">
            <span class="el-radio__control qa-radio-control">
                <input
                    ref="input"
                    class="el-radio__original qa-radio-original"
                    type="radio"
                    role="radio"
                    :name="name"
                    :value="label"
                    :disabled="isDisabled"
                    :checked="isChecked"
                    :tabindex="tabIndex"
                    :aria-checked="isChecked"
                    :aria-disabled="isDisabled"
                    @change="handleChange"
                    @click="handleClick"
                    @focus="handleFocus"
                    @blur="handleBlur"
                />
            </span>
            <span v-if="$slots.default" class="el-radio__label qa-radio-label">
                <slot />
            </span>
        </span>
        <span v-if="$slots.description" class="el-radio__description">
            <slot name="description" />
        </span>
    </label>
</template>

<script setup>
import { computed, inject, ref } from 'vue';

const emit = defineEmits(['update:modelValue', 'change']);
const props = defineProps({
    label: { type: [String, Number, Boolean], default: '' },
    name: { type: String, default: '' },
    modelValue: { type: [String, Number, Boolean], default: false },
    disabled: Boolean,
    showHoverHint: Boolean,
    align: { type: String, default: 'left' },
});

const input = ref(null);
const isFocused = ref(false);
const isActive = ref(false);

const radioGroup = inject('radioGroup', null);
const elFormItem = inject('elFormItem', null);

const isGroup = computed(() => Boolean(radioGroup));
const isDisabled = computed(() => props.disabled || radioGroup?.disabled);
const isChecked = computed(() => {
    return isGroup.value
        ? radioGroup?.modelValue?.value === props.label
        : props.modelValue === props.label;
});
const computedTitle = computed(() => props.showHoverHint ? String(props.label || '') : null);
const tabIndex = computed(() => isDisabled.value ? -1 : 0);

function handleChange(e) {
    if (isGroup.value) {
        radioGroup?.updateModelValue?.(props.label);
        radioGroup?.emitChange?.(props.label);
    } else {
        emit('update:modelValue', props.label);
        emit('change', props.label, e);
    }

    elFormItem?.markAsTouched?.();
    elFormItem?.validate?.('change');

}

function handleFocus() {
    isFocused.value = true;
}
function handleBlur() {
    isFocused.value = false;
    isActive.value = false;
    elFormItem?.validate?.('blur');
}
function handleKeyDown() {
    isActive.value = true;
}
function handleKeyUp() {
    isActive.value = false;
}
function handleClick() {
    if (!input.value?.contains(document.activeElement)) {
        input.value?.focus();
    }
}
</script>

<style lang="pcss" scoped>
.el-radio {
    display: inline-block;
    vertical-align: middle;
    max-width: 100%;
    outline: none;
    position: relative;
    cursor: pointer;
    user-select: none;
    &__content {
        display: flex;
        gap: 8px;
        align-items: center;
        height: inherit;
    }
    &.is-align-center {
        .el-radio__control {
            position: absolute;
            top: 50%;
            inset-inline-start: 16px;
            transform: translateY(-50%);
        }
        .el-radio__content {
            padding-inline-start: 24px;
        }
    }
    &__original {
        width: 0;
        height: 0;
        margin: 0;
        outline: none;
        position: absolute;
        opacity: 0;
    }
    &__description {
        color: var(--av-fixed-light);
        display: block;
        padding-inline-start: 24px;
    }
    &__control {
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 16px;
        width: 16px;
        height: 16px;
        border: 1px solid var(--av-brand-light);
        border-radius: 50%;
        background-color: var(--av-inversed-primary);
        background-clip: content-box
    }
    &__label {
        @mixin body;
        color: var(--av-fixed-secondary);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
        vertical-align: middle;
    }
    &.is-disabled {
        color: var(--av-fixed-lighter);
        cursor: default;
        .el-radio__control {
            background-color: var(--av-brand-accent)
        }
        .el-radio__description, .el-radio__label {
            color: var(--av-fixed-lighter)
        }
    }
    &.is-focus {
        &:not(.is-disabled) {
            .el-radio__control {
                box-shadow: 0 0 0 3px var(--av-fixed-focus);
            }
        }
    }
    &.is-checked {
        .el-radio__control {
            border: 5px solid var(--av-brand-primary)
        }
    }
    &.is-checked.is-disabled {
        .el-radio__control {
            border: 4px solid var(--av-brand-accent);
            background-color: var(--av-brand-light);
            min-width: 14px;
            width: 16px;
            height: 16px;
            &:before {
                content: "";
                display: block;
                min-width: 14px;
                width: 14px;
                height: 14px;
                border-radius: 50%;
                border: 1px solid var(--av-brand-light);
            }
        }
    }
}
</style>
